import PropTypes from "prop-types";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BaseForm, FormSelect } from "components/BaseForm";
import RevenueOpportunityService from "services/RevenueOpportunities";

const LOCK = "lock";
const UNLOCK = "unlock";

const LOCK_STATE_OPTIONS = [
  { key: LOCK, value: LOCK, text: "Lock" },
  { key: UNLOCK, value: UNLOCK, text: "Unlock" },
];

const schema = z.object({
  lock_state: z
    .enum([LOCK, UNLOCK])
    .nullable()
    .refine(value => value != null, "Lock State is required"),
});

/** Admin-only bulk lock or unlock of the selected Revenue Opportunities. */
const RevenueOpportunityLockStateDialog = ({
  open,
  onOpenChange,
  revenueOpportunityIds,
  onSaved,
}) => {
  const changeLockState = useMutation({
    mutationFn: ({ lock_state }) =>
      RevenueOpportunityService.bulkChangeLockState({
        revenue_opportunity_id: revenueOpportunityIds,
        lock_state: lock_state === LOCK,
      }),
  });

  const handleSuccess = () => {
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw:sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change Lock State</DialogTitle>
        </DialogHeader>
        <BaseForm
          schema={schema}
          defaultValues={{ lock_state: null }}
          onSubmit={changeLockState.mutateAsync}
          onSuccess={handleSuccess}
          onCancel={() => onOpenChange(false)}
          submitButtonText="Apply"
        >
          <FormSelect
            name="lock_state"
            label="Lock State"
            required
            options={LOCK_STATE_OPTIONS}
          />
        </BaseForm>
      </DialogContent>
    </Dialog>
  );
};

RevenueOpportunityLockStateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  revenueOpportunityIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default RevenueOpportunityLockStateDialog;
